import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './styles.module.scss';
import { useAppSelector, useRedirectAndScroll } from 'hooks';
import { Partners } from 'components/Partners';
import { useTranslates } from 'providers/translates';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { Language } from 'providers/translates/enums';
import { TProfile } from 'api/http-client/profileTypes';
import { useAuth } from 'providers/auth-provider';

import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';

const committeeArray = [
  {
    name: 'Elder Albert McLeod',
  },
  {
    name: 'Judy Cook',
  },
  {
    name: 'Jennifer Gurke',
  },
  {
    name: 'Louise Halfe',
  },
  {
    name: 'Carolyn Moar',
  },
  {
    name: 'Shahina Siddiqui',
  },
  {
    name: 'Jeroline Smith'
  }
];

/** VAC advisory list (EN). Used when Strapi has empty/null names. */
const vacCommitteeArrayEN = [
  { name: "Colonel (Ret’d) David Barr" },
  { name: 'Shelly Cory' },
  { name: "Kathie King, Staff Sergeant (Ret’d)" },
  { name: 'Deb MacRury' },
  { name: 'Dave MacInnis' },
  { name: 'Trever Slimmon' },
];

/** VAC advisory list (FR). */
const vacCommitteeArrayFR = [
  { name: 'Colonel (à la retraite) David Barr' },
  { name: 'Shelly Cory' },
  { name: "Kathie King, sergente d’état-major (ret.)" },
  { name: 'Deb MacRury' },
  { name: 'Dave MacInnis' },
  { name: 'Trever Slimmon' },
];

const translationsHelmet = {
  descriptionEN: "Learn about Saluting Service's origin, purpose and the partners involved who helped bring the idea to life. ",
  descriptionFR: 'Découvrez d’où vient l’idée du site Saluons leur service, sa raison d’être et les partenaires qui lui ont donné vie.',
  titleEN: 'Saluting Service | About',
  titleFR: 'Saluons leur service | À propos'
} as const;

const AboutPage: React.FC = () => {
  const { translates: t, selectedLang } = useTranslates();
  const [searchParams] = useSearchParams();
  const { onlyScroll } = useRedirectAndScroll({});
  const isFrench = selectedLang === Language.FRENCH;
  const user = useAuth().user as unknown as TProfile;
  const editableQuilt = useAppSelector((state) => state.quilt.editableQuilt);

  useEffect(() => {
    const scrollTo = searchParams?.get('visible');
    if (scrollTo) {
      onlyScroll(scrollTo);
    }
  }, []);

  const nameRow = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRow = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRow = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRow = `Issue or concern:`;
  
  const nameRowVac = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowVac = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRowVac = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowVac = `Issue or concern:`;
  
  const nameRowFr = `Nom associé à votre compte: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowFr = `Adresse courriel associée à votre compt: ${user?.email ?? ''}\n`;
  const quiltNameRowFr = `Nom du carreau de courtepointe pour lequel vous avez besoin d’aide: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowFr = `De quoi s’agit-il?`;
  
  let contactUsEmail: { subject: string; body: string; };

  if (isVac()) {
    contactUsEmail = isFrench ? {
      subject: encodeURIComponent('J’ai une question concernant SaluonsLeurService.ca'),
      body: encodeURIComponent(
        `\n\n\nN.B. – Si vous êtes un utilisateur actuel du site SaluonsLeurService.ca et que vous avez besoin d’assistance, veuillez nous fournir les renseignements suivants pour nous aider à vous faciliter la tâche.\n\n${nameRowFr}${emailRowFr}${quiltNameRowFr}${issueOrConcernRowFr}`
      ),
    } : {
      subject: encodeURIComponent('I have a question about SalutingService.ca'),
      body: encodeURIComponent(
        `\n\n\nNote: If you are a current SalutingService.ca user and are looking for assistance, please provide us with the following information so we can help you as easily as possible:\n\n${nameRowVac}${emailRowVac}${quiltNameRowVac}${issueOrConcernRowVac}`
      ),
    }
  } else {
    contactUsEmail = {
      subject: encodeURIComponent('I have a question about the Canadian Healing Quilt'),
      body: encodeURIComponent(
        `\n\n\nNote: If you are a current Canadian Healing Quilt user and are looking for assistance please provide us with the following information so we can help you as easily as possible:\n\n${nameRow}${emailRow}${quiltNameRow}${issueOrConcernRow}`
      )
    }
  }
  
  const anchorContactUsEmail = () => {
    return (
      <a className={styles.customLink} target="_blank" href={`mailto:${isFrench ? 'info@portailpallatif.ca' : 'info@virtualhospice.ca'}?subject=${contactUsEmail.subject}&body=${contactUsEmail.body}`}>
        {isFrench ? 'info@portailpallatif.ca' : 'info@virtualhospice.ca'}
      </a>
    )
  }

  const legalNoticesLink = (
    <a
      className={styles.customLink}
      href="https://www.virtualhospice.ca/en_US/Main+Site+Navigation/Right+Footer+Navigation/Legal+Notices.aspx"
      target="_blank"
      rel="noreferrer"
    >
      Legal Notices / Privacy Policy
    </a>
  );

  return (
    <>
    {isVac() && (
      <Helmet>
        <meta name="description" content={isFrench ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />
        <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
      </Helmet>
    )}
    <section className="content center">
      <h1 className={styles.titleH1}>{t?.about?.title ?? 'About the Canadian Healing Quilt'}</h1>

      {t?.about?.ideaAndPurposeSection ? (
        <MarkdownRenderer 
          content={t?.about?.ideaAndPurposeSection}
        />
      ) : (
        <>
          <h2 className={styles.titleH2}>The idea</h2>
          <p className={styles.bodyText}>
            During the Covid-19 pandemic Elder Albert McLeod, a member of the Nisichawayasihk Cree Nation, approached 
            the Canadian Virtual Hospice about creating an online space to honour those who had died. He was very concerned 
            that with the growing number of delayed memorials and ceremonies, many people who died during Covid might not 
            be honoured when gathering restrictions were lifted. He envisioned a place where individuals and families could 
            come together online to create a lasting tribute to those who died and to support their healing journey. Drawing 
            from Indigenous culture, he saw a quilt as a universal symbol of comfort and honour. 
          </p>

          <h2 className={styles.titleH2}>The purpose of the Canadian Healing Quilt</h2>
          <p className={styles.bodyText}>
            The Canadian Healing Quilt brings to life Elder Albert’s vision for an online space to remember and honour those who have died.
          </p>
          <p className={styles.bodyText}>
            Healing Quilt.ca,
          </p>
          <ul className='colorNavy'>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Is a place where people living in Canada, and Canadians living around the world, can create memorials for people who have died.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Enables an unlimited number of people to collaborate on a memorial.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Is free. There is no cost to create or maintain a memorial.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>A lasting place to visit, remember and honour.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Allows friends, colleagues and others to share a condolence, a tribute or light a candle in remembrance.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Honours Indigenous traditions and the importance that quilts and blankets have in life’s journeys.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Protects your privacy. Your contact information will never be shared.
            </li>
          </ul>
          <p className={styles.bodyText}>
            Many people have shared with us that due to geography or other reasons, they don’t have a “place” to visit someone who has died. 
            That a “place” would help them in their grief. We hope HealingQuilt.ca can be that place. 
          </p>
        </>
      )}

      <h2 className={styles.titleH2}>{t?.about?.partnersSection?.title ?? 'Partners'}</h2>
      <p className={styles.bodyText}>
        {t?.about?.partnersSection?.description ?? (
          <>
            The Canadian Virtual Hospice is grateful to the Winnipeg Foundation for their investment to develop the Canadian Healing Quilt 
            and to Palliative Manitoba for their support of this project.
          </>
        )}
      </p>
      <Partners />
      <br /><br />
      <h2 className={styles.titleH2}>{t?.about?.committeeMembersTitle ?? 'Advisory committee members'}</h2>
      <div className={`mb-30 ${styles.committee__block}`}>
        {(() => {
          type CommitteeMember = { id?: string | number; name?: string | null };
          const fromCms = ((t?.about?.committeeMembers || []) as CommitteeMember[])
            .filter((member) => Boolean(member?.name));
          const fallback: CommitteeMember[] = isVac()
            ? (isFrench ? vacCommitteeArrayFR : vacCommitteeArrayEN)
            : committeeArray;
          const members = fromCms.length > 0 ? fromCms : fallback;

          return members.map((member, index) => (
            <div key={member.id ?? member.name ?? index} className='colorNavy'>
              <p className={styles.committee__name}>{member.name}</p>
            </div>
          ));
        })()}
      </div>

      {t?.about?.feedbackAndPrivacySection ? (
        <MarkdownRenderer 
          content={t?.about?.feedbackAndPrivacySection}
          anchorContactUsEmail={anchorContactUsEmail}
        />
      ) : (
        <>
          <h2 className={styles.titleH2}>We welcome your feedback</h2>
          <p className={styles.bodyText}>
            We hope you’ll take some time to share your experience, whether creating or viewing a memorial or adding a tribute or condolence. 
            We also appreciate hearing how the quilt has impacted you. Share your feedback, questions, or suggestions by emailing
            {' '}
            {anchorContactUsEmail()}
            .
          </p>
          <h2 id='notice' className={styles.titleH2}>Privacy and Use Notice</h2>
          <p className={styles.bodyText}>Last Updated: September 2024</p>
          <p className={styles.bodyText}>
            The Canadian Healing Quilt is operated by the Canadian Virtual Hospice, a division 
            of the International Centre for Dignity and Palliative Care, a registered charity.
          </p>
          <p className={styles.bodyText}>
            By using the Canadian Healing Quilt you are agreeing to the{' '}{legalNoticesLink}.
          </p>
          <p className={styles.bodyText}>By using the Canadian Healing Quilt, you also confirm the following:</p>
          <ul className="colorNavy">
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>if you post to a Healing Quilt, that you have the right 
              to post the photos and contents you are posting to a Healing Quilt; and
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>your use of the Canadian Healing Quilt will not be exploitive, 
              obscene, harmful, threatening, abusive, harassing, hateful, defamatory, sexually explicit or pornographic, 
              violent, inflammatory, or discriminatory based on race, sex, religion, nationality, disability, sexual orientation, 
              or age or other such legally prohibited ground or be otherwise objectionable, such determination to be made in 
              Canadian Virtual Hospice’s sole discretion.
            </li>
          </ul>
          <p className={styles.bodyText}>
            For more information, please see the{' '}{legalNoticesLink}.
          </p>
          <p className={styles.bodyText}>
            If you have any questions about the Canadian Healing Quilt or the privacy practices of Canadian Virtual Hospice, please contact{' '}
            {anchorContactUsEmail()}
            .
          </p>
        </>
      )}
    </section>
    </>
  );
};

export default AboutPage;
